import type { Rule } from '../../core/assetType.js';
import type { ValidationIssue } from '../../core/issues.js';
import type { Gameshow } from '../../schemas/gameshow.js';

import { toPointer } from '../../core/issues.js';
import { hasNonEmptyDialogue } from './_helpers.js';

const INTRO_NODE_ID = 'intro';
const EXPECTED_OPTION_COUNT = 3;
const EXPECTED_FIRST_ROUND_ID = 'q1';

interface IntroOption {
  outcomeId?: unknown;
}

export const introFlow: Rule<Gameshow> = {
  code: 'gameshow.intro',
  check(input): ValidationIssue[] {
    const issues: ValidationIssue[] = [];
    const nodesById = new Map(input.nodes.map((n, idx) => [n.id, { node: n, idx }]));
    const introEntry = nodesById.get(INTRO_NODE_ID);

    if (!introEntry) {
      const path: (string | number)[] = ['nodes'];
      issues.push({
        code: 'gameshow.intro.missing',
        severity: 'error',
        message: `Expected an '${INTRO_NODE_ID}' node between start-node and ${EXPECTED_FIRST_ROUND_ID}.`,
        path,
        pointer: toPointer(path),
        constraint: { kind: 'custom', detail: `intro node with id '${INTRO_NODE_ID}' required` },
      });
      return issues;
    }

    const { node: intro, idx: introIdx } = introEntry;

    if (!hasNonEmptyDialogue(intro.narratorDialogue)) {
      const path: (string | number)[] = ['nodes', introIdx, 'narratorDialogue'];
      issues.push({
        code: 'gameshow.intro.missing-narrator',
        severity: 'error',
        message: `Intro node '${intro.id}' must have non-empty narratorDialogue.`,
        path,
        pointer: toPointer(path),
        locator: { nodeId: intro.id },
        constraint: { kind: 'shape', detail: 'narratorDialogue required' },
      });
    }

    if (intro.activity?.type !== 'dialogue') {
      const path: (string | number)[] = ['nodes', introIdx, 'activity', 'type'];
      issues.push({
        code: 'gameshow.intro.wrong-activity',
        severity: 'error',
        message: `Intro node '${intro.id}' must use activity type 'dialogue', found '${intro.activity?.type ?? '(missing)'}'.`,
        path,
        pointer: toPointer(path),
        locator: { nodeId: intro.id },
        currentValue: intro.activity?.type,
        constraint: { kind: 'enum', allowedValues: ['dialogue'] },
      });
      return issues;
    }

    const setup = (intro.activity?.setup ?? {}) as Record<string, unknown>;
    const opts = Array.isArray(setup.options) ? (setup.options as IntroOption[]) : null;
    if (!opts || opts.length !== EXPECTED_OPTION_COUNT) {
      const path: (string | number)[] = ['nodes', introIdx, 'activity', 'setup', 'options'];
      issues.push({
        code: 'gameshow.intro.option-count',
        severity: 'error',
        message: `Intro node '${intro.id}' must have exactly ${EXPECTED_OPTION_COUNT} attitude options, found ${opts?.length ?? 0}.`,
        path,
        pointer: toPointer(path),
        locator: { nodeId: intro.id },
        currentValue: opts?.length ?? 0,
        constraint: { kind: 'range', detail: `exactly ${EXPECTED_OPTION_COUNT}` },
      });
      return issues;
    }

    const replyTargets: string[] = [];
    for (const opt of opts) {
      const outcomeId = typeof opt.outcomeId === 'string' ? opt.outcomeId : undefined;
      const target = outcomeId ? intro.exitRoutes?.[outcomeId] : undefined;
      if (typeof target === 'string' && target) {
        replyTargets.push(target);
      }
    }

    if (replyTargets.length !== EXPECTED_OPTION_COUNT) {
      const path: (string | number)[] = ['nodes', introIdx, 'exitRoutes'];
      issues.push({
        code: 'gameshow.intro.routes',
        severity: 'error',
        message: `Intro node '${intro.id}' must route each of its ${EXPECTED_OPTION_COUNT} options to a reply node.`,
        path,
        pointer: toPointer(path),
        locator: { nodeId: intro.id },
        constraint: { kind: 'shape', detail: 'one exitRoute per option' },
      });
      return issues;
    }

    const player = input.characters.find(c => c.role === 'self');
    const contestant = input.characters.find(c => c.role === 'rival');
    const playerName = player?.name;
    const contestantName = contestant?.name;

    for (const replyId of replyTargets) {
      const entry = nodesById.get(replyId);
      if (!entry) {
        continue;
      }
      const { node: reply, idx: replyIdx } = entry;

      const dialogue = Array.isArray(reply.characterDialogue) ? reply.characterDialogue : [];
      if (dialogue.length < 2) {
        const path: (string | number)[] = ['nodes', replyIdx, 'characterDialogue'];
        issues.push({
          code: 'gameshow.intro.reply-missing-pair',
          severity: 'error',
          message: `Intro reply '${replyId}' must have paired characterDialogue (player + contestant).`,
          path,
          pointer: toPointer(path),
          locator: { nodeId: replyId },
          constraint: { kind: 'shape', detail: 'at least 2 dialogue lines required' },
        });
        continue;
      }

      const namesInReply = dialogue.map(d => d.name);
      if (playerName && !namesInReply.includes(playerName)) {
        const path: (string | number)[] = ['nodes', replyIdx, 'characterDialogue'];
        issues.push({
          code: 'gameshow.intro.reply-missing-player',
          severity: 'error',
          message: `Intro reply '${replyId}' missing a characterDialogue line from the player ('${playerName}').`,
          path,
          pointer: toPointer(path),
          locator: { nodeId: replyId },
          constraint: { kind: 'custom', detail: `dialogue from '${playerName}' required` },
        });
      }
      if (contestantName && !namesInReply.includes(contestantName)) {
        const path: (string | number)[] = ['nodes', replyIdx, 'characterDialogue'];
        issues.push({
          code: 'gameshow.intro.reply-missing-contestant',
          severity: 'error',
          message: `Intro reply '${replyId}' missing a characterDialogue line from the contestant ('${contestantName}').`,
          path,
          pointer: toPointer(path),
          locator: { nodeId: replyId },
          constraint: { kind: 'custom', detail: `dialogue from '${contestantName}' required` },
        });
      }

      if (reply.activity?.type !== 'base_activity') {
        const path: (string | number)[] = ['nodes', replyIdx, 'activity', 'type'];
        issues.push({
          code: 'gameshow.intro.reply-wrong-activity',
          severity: 'error',
          message: `Intro reply '${replyId}' must use activity type 'base_activity', found '${reply.activity?.type ?? '(missing)'}'.`,
          path,
          pointer: toPointer(path),
          locator: { nodeId: replyId },
          currentValue: reply.activity?.type,
          constraint: { kind: 'enum', allowedValues: ['base_activity'] },
        });
      }

      const exitTarget = reply.exitRoutes ? Object.values(reply.exitRoutes)[0] : undefined;
      if (exitTarget !== EXPECTED_FIRST_ROUND_ID) {
        const path: (string | number)[] = ['nodes', replyIdx, 'exitRoutes'];
        issues.push({
          code: 'gameshow.intro.reply-wrong-exit',
          severity: 'error',
          message: `Intro reply '${replyId}' must exit to '${EXPECTED_FIRST_ROUND_ID}', exits to '${exitTarget ?? '(missing)'}'.`,
          path,
          pointer: toPointer(path),
          locator: { nodeId: replyId },
          currentValue: exitTarget,
          constraint: { kind: 'enum', allowedValues: [EXPECTED_FIRST_ROUND_ID] },
        });
      }
    }

    return issues;
  },
};
